import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  Shield,
  CheckCircle,
  AlertTriangle,
  Smartphone,
  Mail,
  Monitor,
  MapPin,
  Clock,
  LogOut,
  Key,
  Globe,
  HelpCircle,
  Download,
  RefreshCw,
  Eye,
  Copy,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import TOTPSetupModal from '../modals/TOTPSetupModal';

const SecurityTab = ({ user }) => {
  const [securityStatus, setSecurityStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTOTPModal, setShowTOTPModal] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      // Load security status
      const statusResponse = await axios.get(
        `${BACKEND_URL}/api/settings/security/status`,
        { withCredentials: true }
      );
      setSecurityStatus(statusResponse.data);

      // Load sessions
      const sessionsResponse = await axios.get(
        `${BACKEND_URL}/api/settings/security/sessions`,
        { withCredentials: true }
      );
      setSessions(sessionsResponse.data.sessions);

    } catch (error) {
      console.error('Failed to load security data:', error);
      toast.error('Failed to load security information');
    }
  };

  const handleTOTPSetupSuccess = async () => {
    toast.success('✓ Two-factor authentication enabled successfully!', {
      duration: 4000,
      position: 'top-center'
    });
    await loadSecurityData();
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    const password = window.prompt('Enter your password to disable 2FA:');
    if (!password) return;

    setIsLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/settings/security/totp/disable`,
        { password },
        { withCredentials: true }
      );
      
      toast.success('✓ Two-factor authentication disabled', {
        duration: 3000,
        position: 'top-center'
      });
      await loadSecurityData();
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBackupCodes = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/settings/security/totp/backup-codes`,
        { withCredentials: true }
      );
      setBackupCodes(response.data.masked_codes || []);
      setShowBackupCodes(true);
    } catch (error) {
      console.error('Failed to load backup codes:', error);
      toast.error('Failed to load backup codes');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleRevokeSession = async (deviceId) => {
    setIsLoading(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/settings/security/sessions/${deviceId}`,
        { withCredentials: true }
      );
      
      toast.success('Session revoked successfully');
      await loadSecurityData();
    } catch (error) {
      console.error('Failed to revoke session:', error);
      toast.error('Failed to revoke session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRevokeAllSessions = async () => {
    setIsLoading(true);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/settings/security/sessions`,
        { withCredentials: true }
      );
      
      toast.success('All other sessions revoked successfully');
      await loadSecurityData();
    } catch (error) {
      console.error('Failed to revoke all sessions:', error);
      toast.error('Failed to revoke sessions');
    } finally {
      setIsLoading(false);
    }
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp || timestamp === 'Just now') return 'Just now';
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    } catch {
      return 'Unknown';
    }
  };

  if (!securityStatus) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <Shield className="w-8 h-8 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading security information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Status Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-blue-600" />
            <span>Security Overview</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Account Email</p>
                  <p className="text-sm text-gray-500">{securityStatus.email}</p>
                </div>
              </div>
              {securityStatus.email_verified ? (
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Unverified
                </Badge>
              )}
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">Last Login</p>
                  <p className="text-sm text-gray-500">
                    {securityStatus.last_login ? getRelativeTime(securityStatus.last_login) : 'Never'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication (TOTP) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Smartphone className="w-5 h-5 text-blue-600" />
              <span>Two-Factor Authentication</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="w-64">TOTP (Time-based One-Time Password) adds an extra security layer using apps like Google Authenticator or Authy.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {securityStatus.two_fa_enabled ? (
              <Badge className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
                <CheckCircle className="w-3 h-3 mr-1" />
                Enabled
              </Badge>
            ) : (
              <Badge className="bg-gray-100 text-gray-600 border border-gray-200 px-3 py-1 rounded-full text-xs font-medium">
                Off
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Add an extra layer of security using an authenticator app. 
              You'll need to enter a 6-digit code when signing in.
            </p>

            {securityStatus.two_fa_enabled ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl space-y-3">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">2FA is Active</span>
                </div>
                <p className="text-sm text-green-700">
                  Your account is protected with authenticator app verification.
                </p>
                {securityStatus.backup_codes_remaining !== undefined && securityStatus.backup_codes_remaining > 0 && (
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                    <div className="flex items-center space-x-2">
                      <Key className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">
                        {securityStatus.backup_codes_remaining} backup codes remaining
                      </span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={loadBackupCodes}
                      className="text-green-700 hover:text-green-800 hover:bg-green-100"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </div>
                )}
                <div className="flex space-x-2 pt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDisable2FA}
                    disabled={isLoading}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Disable 2FA
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-start space-x-3 mb-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900 mb-1">Strengthen Your Security</p>
                    <p className="text-sm text-blue-700">
                      Enable 2FA to add an extra layer of protection. Even if someone knows your password, 
                      they won't be able to access your account without your phone.
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => setShowTOTPModal(true)}
                >
                  <Smartphone className="w-4 h-4 mr-2" />
                  Enable Two-Factor Authentication
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Login Methods */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-blue-600" />
            <span>Login Methods</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Manage how you sign into your Payo account.
            </p>

            <div className="space-y-3">
              {/* Email/Password */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Email & Password</p>
                    <p className="text-sm text-gray-500">Sign in with your email and password</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Enabled
                </Badge>
              </div>

              {/* Google Sign-In */}
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">Google Sign-In</p>
                    <p className="text-sm text-gray-500">Sign in with your Google account</p>
                  </div>
                </div>
                <Badge className="bg-green-100 text-green-800 border-green-200">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Available
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TOTP Setup Modal */}
      <TOTPSetupModal
        isOpen={showTOTPModal}
        onClose={() => setShowTOTPModal(false)}
        onSuccess={handleTOTPSetupSuccess}
      />

      {/* Backup Codes Modal */}
      {showBackupCodes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Backup Codes</h3>
              <button
                onClick={() => setShowBackupCodes(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              These are your emergency backup codes. Each code can only be used once.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {backupCodes.map((code, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                >
                  <code className="text-sm font-mono">{code}</code>
                  <button
                    onClick={() => copyToClipboard(code)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <Button
              onClick={() => setShowBackupCodes(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityTab;